import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { changeAdminPasswordMutationOptions } from '@/features/admins/api/change-admin-password.api';

export interface ChangeAdminPasswordDialogProps extends BaseDialogProps {
  adminId: string;
}

export default function ChangeAdminPasswordDialog({
  dialog,
  adminId,
}: ChangeAdminPasswordDialogProps) {
  const { closeChangeAdminPasswordDialog } = useV2DialogContext();

  const changePasswordMutation = useMutation({
    ...changeAdminPasswordMutationOptions(),
    onSuccess: () => {
      toast.success('Berhasil mengubah password admin');
      closeChangeAdminPasswordDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal mengubah password admin';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      password: '',
      password_confirmed: '',
    },
    onSubmit: async ({ value }) => {
      if (value.password !== value.password_confirmed) {
        toast.error('Konfirmasi password tidak cocok');
        return;
      }
      if (value.password.length < 6) {
        toast.error('Password minimal 6 karakter');
        return;
      }

      await changePasswordMutation.mutateAsync({
        id: adminId,
        data: {
          password: value.password,
          password_confirmed: value.password_confirmed,
        },
      });
    },
  });

  const isPending = changePasswordMutation.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeChangeAdminPasswordDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ubah Password Admin</DialogTitle>
          <DialogDescription>
            Masukkan password baru untuk admin ini.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field name="password">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Password Baru</Label>
                <Input
                  id={field.name}
                  type="password"
                  placeholder="Masukkan Password Baru"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="password_confirmed">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Konfirmasi Password</Label>
                <Input
                  id={field.name}
                  type="password"
                  placeholder="Masukkan Ulang Password Baru"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeChangeAdminPasswordDialog}
              disabled={isPending}
            >
              Batal
            </Button>
            <form.Subscribe selector={state => [state.canSubmit]}>
              {([canSubmit]) => (
                <Button type="submit" disabled={!canSubmit || isPending}>
                  {isPending ? 'Menyimpan...' : 'Simpan'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
